import { create } from 'zustand';
import {
  createLocation,
  listLocations,
  transitionLocation,
  updateLocation,
} from '@/lib/locations';
import type { Location, LocationStatus } from '@/lib/types';

/**
 * Locations store
 *
 * Lightweight client cache of the current org's locations. Hydrated by
 * loadLocations(); kept fresh by mutations through this store. Components
 * read via the selectors at the bottom.
 *
 * For Day 3 this is just a basic cache; Day 4+ will add React Query for
 * proper invalidation patterns.
 */

interface LocationsState {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  items: Location[];
  error: string | null;

  load: () => Promise<void>;
  add: (input: {
    name: string;
    address?: string | null;
    phone?: string | null;
    timezone?: string | null;
    shift_split_time?: string | null;
  }) => Promise<Location>;
  update: (
    id: string,
    patch: Partial<
      Pick<
        Location,
        'name' | 'address' | 'phone' | 'timezone' | 'shift_split_time'
      >
    >
  ) => Promise<Location>;
  transition: (
    id: string,
    newStatus: LocationStatus,
    reason: string
  ) => Promise<Location>;
  reset: () => void;
}

export const useLocations = create<LocationsState>((set) => ({
  status: 'idle',
  items: [],
  error: null,

  async load() {
    set({ status: 'loading', error: null });
    try {
      const items = await listLocations();
      set({ status: 'loaded', items });
    } catch (err) {
      set({ status: 'error', error: (err as Error).message });
    }
  },

  async add(input) {
    const created = await createLocation(input);
    set((s) => ({ items: [...s.items, created] }));
    return created;
  },

  async update(id, patch) {
    const updated = await updateLocation(id, patch);
    set((s) => ({
      items: s.items.map((l) => (l.id === id ? updated : l)),
    }));
    return updated;
  },

  async transition(id, newStatus, reason) {
    const updated = await transitionLocation(id, newStatus, reason);
    set((s) => ({
      items: s.items.map((l) => (l.id === id ? updated : l)),
    }));
    return updated;
  },

  reset() {
    set({ status: 'idle', items: [], error: null });
  },
}));

// ----- selectors ------------------------------------------------------------

export const useLocationCount = () => useLocations((s) => s.items.length);

export const useActiveLocations = () =>
  useLocations((s) =>
    s.items.filter((l) => l.status === 'active' || l.status === 'draft')
  );
